"use client"

import {
    Card, CardHeader, CardTitle, CardDescription, CardContent
} from "../../../components/ui/card"
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger
} from "../../../components/ui/dialog"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Textarea } from "../../../components/ui/textarea"
import { Checkbox } from "../../../components/ui/checkbox"
import { Label } from "../../../components/ui/label"
import { RefreshCw, Plus, Edit, Trash, EyeOff, Eye } from "lucide-react"
import { useState } from "react"
import { Toast } from "../../../components/Toast"
import {
    submitDailyDeclaration,
    deleteDailyDeclaration,
    // updateDailyDeclaration, // optional if you want to keep separate update
} from "../../../api/adminService"

const ITEMS_PER_PAGE = 5

export function DailyDeclarationSection({ declarations = [], setDeclarations, supplementaryItems = [], handleSync, statusMessage, isSyncing, onUpdateSupplementary }: any) {
    const [selectedItem, setSelectedItem] = useState<any | null>(null)
    const [formData, setFormData] = useState<any>({})
    const [newItemOpen, setNewItemOpen] = useState(false)
    const [newForm, setNewForm] = useState<any>({
        date: "",
        shortWord: "",
        bibleReference: "",
        declarations: []
    })
    const [currentPage, setCurrentPage] = useState(1)
    const [expandedItems, setExpandedItems] = useState<string[]>([])
    const [includeSupplementary, setIncludeSupplementary] = useState(true)

    const totalPages = Math.ceil(declarations.length / ITEMS_PER_PAGE)
    const paginatedItems = declarations.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    )

    const toggleExpand = (id: string) => {
        setExpandedItems(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        )
    }

    const handleUpdate = async () => {
        try {
            const payload = {
                ...formData,
                isManualUpdate: true,
            }
            await submitDailyDeclaration(payload)
            setDeclarations((prev: any) =>
                prev.map((item: any) => (item.id === payload.id ? payload : item))
            )
            setSelectedItem(null)
            Toast.success("Daily declaration updated successfully")
        } catch (err) {
            Toast.error("Update failed: " + err)
        }
    }

    const handleCreate = async () => {
        try {
            const payload = {
                ...newForm,
                declarations: Array.isArray(newForm.declarations)
                    ? newForm.declarations
                    : (newForm.declarations || "").split("\n").map((d: string) => d.trim()).filter(Boolean),
            }
            const result = await submitDailyDeclaration(payload)
            setDeclarations((prev: any) => [{ ...payload, id: result.id }, ...prev])
            setNewForm({ date: "", shortWord: "", bibleReference: "", declarations: [] })
            setNewItemOpen(false)
            Toast.success("Daily declaration created successfully")
        } catch (err) {
            Toast.error("Create failed: " + err)
        }
    }

    const handleDelete = async (id: string) => {
        try {
            await deleteDailyDeclaration(id)
            setDeclarations((prev: any) => prev.filter((i: any) => i.id !== id))
            Toast.success("Declaration deleted")
        } catch (err) {
            Toast.error("Delete failed: " + err)
        }
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">📖 Daily Declarations</CardTitle>
                            <CardDescription>Manage daily scripture declarations and references</CardDescription>
                        </div>
                        <Dialog open={newItemOpen} onOpenChange={setNewItemOpen}>
                            <DialogTrigger asChild>
                                <Button><Plus className="h-4 w-4 mr-2" /> New Declaration</Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Create New Daily Declaration</DialogTitle>
                                    <DialogDescription>Fill in details for the new daily declaration</DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium">Date</label>
                                        <Input
                                            type="date"
                                            value={newForm.date}
                                            onChange={(e) => setNewForm({ ...newForm, date: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Short Word (Title)</label>
                                        <Input
                                            value={newForm.shortWord}
                                            onChange={(e) => setNewForm({ ...newForm, shortWord: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Bible Reference</label>
                                        <Input
                                            value={newForm.bibleReference}
                                            onChange={(e) => setNewForm({ ...newForm, bibleReference: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Declarations (one per line)</label>
                                        <Textarea
                                            value={Array.isArray(newForm.declarations) ? newForm.declarations.join("\n") : newForm.declarations}
                                            onChange={(e) => setNewForm({ ...newForm, declarations: e.target.value })}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button variant="outline" onClick={() => setNewItemOpen(false)}>Cancel</Button>
                                    <Button onClick={handleCreate}>Create</Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </div>
                </CardHeader>

                <CardContent className="space-y-6">
                    {/* Items */}
                    <div className="space-y-4">
                        {paginatedItems.map((item: any) => (
                            <div key={item.id} className="border rounded p-4 space-y-2 bg-muted/10">
                                {/* <p>{JSON.stringify(item)}</p> */}
                                <div className="flex justify-between items-center">
                                    <h4 className="font-medium">{item.date} – {item.shortWord}</h4>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => toggleExpand(item.id)}
                                        >
                                            {expandedItems.includes(item.id) ? "Collapse" : "Expand"}
                                        </Button>
                                        <Dialog open={!!selectedItem} onOpenChange={(isOpen) => !isOpen && setSelectedItem(null)}>
                                            <DialogTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedItem(item)
                                                        setFormData({ ...item })
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                            </DialogTrigger>
                                            <DialogContent className="max-w-2xl">
                                                <DialogHeader>
                                                    <DialogTitle>Edit Declaration</DialogTitle>
                                                </DialogHeader>
                                                {selectedItem && (
                                                    <div className="space-y-4">
                                                        <Input
                                                            type="date"
                                                            value={formData.date || ""}
                                                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                                        />
                                                        <Input
                                                            placeholder="Short Word"
                                                            value={formData.shortWord || ""}
                                                            onChange={(e) => setFormData({ ...formData, shortWord: e.target.value })}
                                                        />
                                                        <Input
                                                            placeholder="Bible Reference"
                                                            value={formData.bibleReference || ""}
                                                            onChange={(e) => setFormData({ ...formData, bibleReference: e.target.value })}
                                                        />
                                                        <Textarea
                                                            value={formData.declarations?.join("\n") || ""}
                                                            onChange={(e) => setFormData({
                                                                ...formData,
                                                                declarations: e.target.value.split("\n").map((d) => d.trim())
                                                            })}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex justify-end gap-2 pt-4">
                                                    <Button variant="outline" onClick={() => setSelectedItem(null)}>Cancel</Button>
                                                    <Button onClick={handleUpdate}>Update</Button>
                                                </div>
                                            </DialogContent>
                                        </Dialog>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleDelete(item.id)}
                                        >
                                            <Trash className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>

                                {expandedItems.includes(item.id) && (
                                    <div className="text-sm text-muted-foreground space-y-1">
                                        <p><strong>Reference:</strong> {item.bibleReference}</p>
                                        <ul className="list-disc ml-5">
                                            {item.declarations?.map((d: string, idx: number) => (
                                                <li key={idx}>{d}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Pagination + Sync */}
                    <div className="flex items-center justify-between pt-4">
                        <p className="text-sm text-muted-foreground">
                            Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
                            {Math.min(currentPage * ITEMS_PER_PAGE, declarations.length)} of {declarations.length}
                        </p>
                        <div className="flex items-center gap-1">
                            <Button size="sm" variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</Button>
                            <Button size="sm" variant="outline" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</Button>
                            {[...Array(totalPages)].map((_, idx) => {
                                const page = idx + 1
                                return (
                                    <Button
                                        key={page}
                                        variant={currentPage === page ? "default" : "outline"}
                                        size="sm"
                                        onClick={() => setCurrentPage(page)}
                                    >
                                        {page}
                                    </Button>
                                )
                            }).slice(Math.max(0, currentPage - 3), Math.min(totalPages, currentPage + 2))}
                            <Button size="sm" variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</Button>
                            <Button size="sm" variant="outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}